import { addDays, format, isValid, parse } from 'date-fns';
import { DEFAULT_DATE_FORMAT, DEFAULT_DATE_REST_FORMAT } from '../constants/app.constants';

/**
 * Formats a Date object into a predefined string format.
 * Gracefully handles null, undefined, or invalid Date objects.
 *
 * @param {Date | null | undefined} d The date to format.
 * @returns {string | undefined} The formatted date string, or undefined if the input is invalid.
 * @example
 * simplifyDate(new Date('2025-07-18T16:23:20')) // "2025-07-18" (záleží na formátu)
 * simplifyDate(new Date('invalid date'))     // undefined
 * simplifyDate(null)                       // undefined
 */
export const simplifyDate = (d: Date | null | undefined): string | undefined => {
  if (d instanceof Date && !isNaN(d.getTime())) {
    return format(d, DEFAULT_DATE_REST_FORMAT);
  }

  return undefined;
};

export const parseStringToDate = (value: string): Date | null => {
  if (value) {
    const dt = parse(value, DEFAULT_DATE_REST_FORMAT, new Date());
    if (isValid(dt)) {
      return dt;
    }
  }
  return null;
};

export function convertCzDateToIso(czDate: string): string | null {
  try {
    // Použijeme `parse` pro převedení z českého formátu na Date objekt
    const parsedDate = parse(czDate, DEFAULT_DATE_FORMAT, new Date());

    // Zkontrolujeme, jestli je datum platné
    if (isNaN(parsedDate.getTime())) {
      return null; // Neplatné datum
    }

    // Použijeme `format` pro převod na ISO formát
    return format(parsedDate, DEFAULT_DATE_REST_FORMAT);
  } catch (error) {
    console.error('Error parsing date:', error);
    return null; // Pokud se vyskytne nějaká chyba, vrátíme null
  }
}

/**
 * Funkce pro rozpoznání zjednodušených formátů dat a jejich převod na plný formát (dd.MM.yyyy).
 *
 * Podporované formáty vstupu:
 *
 * 1. `ddMMyy` - Šest znaků, reprezentující den, měsíc a rok (předpokládá se 21. století).
 *    - Příklad: `011024` -> `01.10.2024`
 *
 * 2. `dMyy` nebo `ddMyy` - Pět nebo šest znaků, kde je možné vynechat nulu u dne a měsíce.
 *    - Příklad: `11024` -> `01.10.2024` nebo `011024` -> `01.10.2024`
 *
 * 3. `dMyyyy` nebo `ddMyyyy` - Sedm nebo osm znaků, kde je možné vynechat nulu u dne a měsíce, a obsahuje čtyřmístný rok.
 *    - Příklad: `1102024` -> `01.10.2024` nebo `01102024` -> `01.10.2024`
 *
 * @param dateString - Vstupní datum ve zjednodušeném formátu.
 * @returns {string | null} - Vrátí datum ve formátu `dd.MM.yyyy`, pokud je vstup validní, jinak `null`.
 */
export function convertSimplifiedDate(dateString: string): string | null {
  // Formát ddMMyy (např. 011024 -> 01.10.2024)
  const simplifiedDatePattern = /^\d{6}$/;
  if (simplifiedDatePattern.test(dateString)) {
    const day = dateString.substring(0, 2);
    const month = dateString.substring(2, 4);
    const year = '20' + dateString.substring(4, 6); // Předpokládáme 21. století

    return `${day}.${month}.${year}`;
  }

  // Formát dMyy nebo ddMyy (např. 11024 nebo 011024 -> 01.10.2024)
  const shortDatePattern = /^\d{1,2}\d{1,2}\d{2}$/;
  if (shortDatePattern.test(dateString)) {
    const day = dateString.length === 6 ? dateString.substring(0, 2) : '0' + dateString.substring(0, 1);
    const month = dateString.length === 6 ? dateString.substring(2, 4) : '0' + dateString.substring(1, 2);
    const year = '20' + dateString.slice(-2); // Předpokládáme 21. století

    return `${day}.${month}.${year}`;
  }

  // Formát dMyyyy nebo ddMyyyy (např. 1102024 nebo 01102024 -> 01.10.2024)
  const longDatePattern = /^\d{1,2}\d{1,2}\d{4}$/;
  if (longDatePattern.test(dateString)) {
    const day = dateString.length === 8 ? dateString.substring(0, 2) : '0' + dateString.substring(0, 1);
    const month = dateString.length === 8 ? dateString.substring(2, 4) : '0' + dateString.substring(1, 2);
    const year = dateString.slice(-4); // Přímé přečtení čtyřmístného roku

    return `${day}.${month}.${year}`;
  }

  // Pokud není formát rozpoznán, vrátí null
  return null;
}

export function createDate(): Date;
export function createDate(daysOffset: number, asString: true): string;
export function createDate(daysOffset = 0, asString = false): Date | string {
  // Posune „dnešní“ datum o daysOffset dní
  const dateRaw = addDays(new Date(), daysOffset);

  if (asString) {
    // Naformátuje na řetězec YYYY-MM-DD
    return format(dateRaw, 'yyyy-MM-dd');
  }

  // Vrátí Date objekt
  return dateRaw;
}
